import { ChangeEvent, useMemo } from "react";
import Form from "react-bootstrap/Form";
import classNames from "classnames";
import { SelectOption } from "@idw-types/common";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ClickableIcon } from "../clickable-icon/clickable-icon";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { IdwFormLabelWithIconContainer } from "../idw-form-label-with-icon-container/idw-form-label-with-icon-container";
import styles from "./radio-input-form-group.less";

type RadioInputFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    className?: string;
    automationId?: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    isRequired: boolean;
    options: Array<SelectOption>;
    optionTooltips?: Array<string>;
    layout?: LayoutTypes;
    radioButtonLayout?: LayoutTypes;
    afterLabelTooltipText?: string;
    afterLabelIcon?: IconDefinition;
    afterLabelIconDisabled?: boolean;
    afterLabelIconOnClick?: () => void;
    afterInputIcon?: IconDefinition;
    afterInputIconDisabled?: boolean;
    afterInputIconOnClick?: () => void;
    rightAlignedLabelElement?: React.ReactNode;
};

export const RadioInputFormGroup = (props: RadioInputFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        label,
        value,
        onChange,
        disabled,
        isRequired,
        options,
        optionTooltips,
        layout,
        radioButtonLayout,
        afterLabelTooltipText,
        afterLabelIcon,
        afterLabelIconDisabled,
        afterLabelIconOnClick,
        afterInputIcon,
        afterInputIconDisabled,
        afterInputIconOnClick,
        rightAlignedLabelElement
    } = props;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        onChange(value);
    };

    const formGroupClassName = useMemo(() => {
        return layout === LayoutTypes.COLUMN ? styles.radioInputFormGroupColumn : styles.radioInputFormGroupRow;
    }, [layout]);

    const radioButtonGroupClassName = useMemo(() => {
        return radioButtonLayout === LayoutTypes.COLUMN ? styles.radioButtonGroupColumn : styles.radioButtonGroupRow;
    }, [radioButtonLayout]);

    return (
        <Form.Group className={classNames(formGroupClassName, className)}>
            {label && (
                <IdwFormLabelWithIconContainer
                    controlId={controlId}
                    labelWidth={labelWidth}
                    layout={layout}
                    label={label}
                    isRequired={isRequired}
                    afterLabelTooltipText={afterLabelTooltipText}
                    afterLabelIcon={afterLabelIcon}
                    afterLabelIconDisabled={afterLabelIconDisabled}
                    afterLabelIconOnClick={afterLabelIconOnClick}
                    rightAlignedLabelElement={rightAlignedLabelElement}
                />
            )}
            <div className={radioButtonGroupClassName}>
                {options.map((option, index) => {
                    const label = optionTooltips?.[index]
                        ? (
                            <IdwFormLabelWithIconContainer
                                controlId={controlId}
                                labelWidth={labelWidth}
                                layout={layout}
                                label={option.label}
                                isRequired={isRequired}
                                afterLabelTooltipText={optionTooltips[index]}
                            />
                        )
                        : option.label;

                    return (
                        <Form.Check
                            id={`${controlId}-${index}`}
                            className={styles.radioButton}
                            required={isRequired}
                            key={`${controlId}-${index}`}
                            automation-id={`${automationId}-${index}`}
                            aria-label={option.label}
                            label={label}
                            onChange={handleChange}
                            type={"radio"}
                            name={`${controlId}-radio-group`}
                            value={option.value}
                            checked={option.value === value}
                            disabled={disabled}
                        />
                    );
                })}
                {afterInputIcon && afterInputIconOnClick && (
                    <div>
                        <ClickableIcon controlId={controlId} disabled={afterInputIconDisabled} icon={afterInputIcon} onClick={afterInputIconOnClick} className={styles.afterInputIcon} />
                    </div>
                )}
            </div>
        </Form.Group>
    );
};